
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Asset} from './Asset';
import {FieldModelAsset} from './Asset';

/**
 * AccountAssets struct
 */
class AccountAssets {
  /**
   * Initialize struct
   * @param {!number=} login
   * @param {!Map=} assets
   * @constructor
   */
  constructor (login = new UInt64(0, 0), assets = new Map()) {
    this.Login = login
    this.Assets = assets
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AccountAssets}
   * @param {!AccountAssets} other Other struct
   * @returns {!AccountAssets} This struct
   */
  copy (other) {
    if (other.Login != null) {
      this.Login = UInt64.fromNumber(other.Login)
    } else {
      this.Login = null
    }
    if (other.Assets != null) {
      this.Assets = new Map()
      Object.keys(other.Assets).forEach(key => {
        if (key != null) {
          let tempKey
          tempKey = new UUID(key)
          if (other.Assets[key] != null) {
            let tempValue
            tempValue = Asset.fromObject(other.Assets[key])
            this.Assets.set(tempKey, tempValue)
          } else {
            this.Assets.set(tempKey, null)
          }
        }
      })
    } else {
      this.Assets = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AccountAssets}
   * @returns {!AccountAssets} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AccountAssetsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AccountAssetsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AccountAssets}
   * @param {!AccountAssets} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AccountAssets)) {
      throw new TypeError('Instance of AccountAssets is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AccountAssets}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Login: ((this.Login != null) ? this.Login.toNumber() : null),
      Assets: ((this.Assets != null) ? fbe.MapToObject(new Map(Array.from(this.Assets, item => [((item[0] != null) ? item[0].toString() : null), ((item[1] != null) ? item[1] : null)]))) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AccountAssets.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AccountAssets} other Object value
   * @returns {!AccountAssets} Created struct
   */
  static fromObject (other) {
    return new AccountAssets().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AccountAssets}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AccountAssets.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AccountAssets}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 51
  }
}

export { AccountAssets };

/**
 * Fast Binary Encoding AccountAssets field model
 */
class FieldModelAccountAssets extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Login = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.Assets = new fbe.FieldModelMap(new fbe.FieldModelUUID(buffer, this.Login.fbeOffset + this.Login.fbeSize), new FieldModelAsset(buffer, this.Login.fbeOffset + this.Login.fbeSize), buffer, this.Login.fbeOffset + this.Login.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelAccountAssets}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAccountAssets}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Login.fbeSize + this.Assets.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAccountAssets}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Login.fbeExtra + this.Assets.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAccountAssets}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAccountAssets.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAccountAssets}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 51
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAccountAssets}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAccountAssets}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.Assets.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Assets.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Assets.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAccountAssets}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAccountAssets}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAccountAssets}
   * @param {!AccountAssets} fbeValue Default value, defaults is new AccountAssets()
   * @returns {!AccountAssets} AccountAssets value
   */
  get (fbeValue = new AccountAssets()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAccountAssets}
   * @param {!AccountAssets} fbeValue AccountAssets value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.Assets.fbeSize) <= fbeStructSize) {
      this.Assets.get(fbeValue.Assets)
    } else {
      fbeValue.Assets.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Assets.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAccountAssets}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAccountAssets}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAccountAssets}
   * @param {!AccountAssets} fbeValue AccountAssets value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAccountAssets}
   * @param {AccountAssets} fbeValue AccountAssets value
   */
  setFields (fbeValue) {
    this.Login.set(fbeValue.Login)
    this.Assets.set(fbeValue.Assets)
  }
}

export { FieldModelAccountAssets };

/**
 * Fast Binary Encoding AccountAssets model
 */
class AccountAssetsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAccountAssets(this.buffer, 4)
  }

  /**
   * Get the AccountAssets model
   * @this {!AccountAssetsModel}
   * @returns {!FieldModelAccountAssets} model AccountAssets model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AccountAssetsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AccountAssetsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AccountAssetsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AccountAssetsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAccountAssets.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AccountAssetsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AccountAssetsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AccountAssetsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AccountAssetsModel}
   * @param {!AccountAssets} value AccountAssets value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AccountAssetsModel}
   * @param {!AccountAssets} value AccountAssets value, defaults is new AccountAssets()
   * @return {!object} Deserialized AccountAssets value and its size
   */
  deserialize (value = new AccountAssets()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AccountAssets(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AccountAssets(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AccountAssetsModel}
   * @param {!number} prev Previous AccountAssets model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AccountAssetsModel };
